
import { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type SaveFunction<T> = (data: T, session: Session) => Promise<void>;

export const saveWithAuth = async <T>(
  data: T,
  saveFunction: SaveFunction<T>,
  onAuthRequired: () => void
): Promise<boolean> => {
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session) {
    // User is not authenticated, trigger auth modal
    onAuthRequired();
    return false;
  }
  
  try {
    // User is authenticated, proceed with save operation
    await saveFunction(data, session);
    return true;
  } catch (error: any) {
    console.error('Error saving data:', error);
    toast.error('Error saving data: ' + error.message);
    return false;
  }
};

export const isAuthenticated = async (): Promise<boolean> => {
  const { data: { session } } = await supabase.auth.getSession();
  return !!session;
};

export const getCurrentUserId = async (): Promise<string | null> => {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.user?.id || null;
};

/**
 * A hook to save data with different scenarios - used across the app
 */
export const saveDataWithAuth = async <T>(
  data: T,
  entityType: string,
  tableName: string,
  redirectUrl: string
): Promise<boolean> => {
  return saveWithAuth(
    data,
    async (dataToSave, session) => {
      // Type checking to ensure tableName is a valid table name
      const validTables = [
        'rate_calculations',
        'crystal_ball_calculations',
        'artifacts',
        'opportunities',
        'profiles',
        'okrs'
        // Add more valid table names as needed
      ];
      
      if (!validTables.includes(tableName)) {
        throw new Error(`Invalid table name: ${tableName}`);
      }
      
      // Insert data to the appropriate table with type assertion
      const { error } = await supabase
        .from(tableName as any)
        .insert({
          user_id: session.user.id,
          ...dataToSave
        });
        
      if (error) {
        console.error(`Error saving ${entityType}:`, error);
        throw error;
      }
    },
    () => {
      // Redirect to auth with return URL
      window.location.href = `/auth?redirect=${redirectUrl}`;
    }
  );
};
